import { useState, useEffect, useCallback, useMemo } from "react";
import {
  MODULE_REGISTRY,
  ModuleDefinition,
  VALID_MODULE_IDS,
  getModulesByCategory,
} from "@/config/modules";
import { supabase } from "@/integrations/supabase/client";
import { usePremium } from "@/contexts/PremiumContext";
import { useMyWorkAccess } from "@/hooks/useMyWorkAccess";

// ── Types ────────────────────────────────────────────────────────────────────

export interface ResolvedModule extends ModuleDefinition {
  /** Translated display name */
  name: string;
  /** Translated subtitle */
  sub: string;
  /** Whether the module is marked as a favorite by the user */
  isFavorite: boolean;
  /** User-defined position for ordering */
  position: number;
}

interface ModulePreference {
  module_id: string;
  is_visible: boolean;
  is_favorite: boolean;
  position: number;
}

interface UserProfile {
  hasChild: boolean;
}

interface UseModulesResult {
  /** All visible modules (filtered by profile + premium), excluding hidden */
  visibleModules: ResolvedModule[];
  /** Core modules (single flat list) */
  coreModules: ResolvedModule[];
  /** Modules in the Special / Especial section (always visible) */
  specialModules: ResolvedModule[];
  /** User's favorite modules, ordered by position (filtered to valid modules only) */
  favorites: ResolvedModule[];
  /** Toggle a module's favorite status */
  toggleFavorite: (moduleId: string) => Promise<void>;
  /** Whether preferences are still loading */
  isLoading: boolean;
}

// ── Hook ─────────────────────────────────────────────────────────────────────

export function useModules(
  t: (key: string) => string,
): UseModulesResult {
  const { isPremium } = usePremium();
  const { allowed: myWorkAllowed, isLoading: myWorkLoading } = useMyWorkAccess();
  const [preferences, setPreferences] = useState<ModulePreference[]>([]);
  const [profile, setProfile] = useState<UserProfile>({ hasChild: false });
  const [isLoading, setIsLoading] = useState(true);
  const [userId, setUserId] = useState<string | null>(null);

  // Load user profile and preferences on mount
  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      if (!user || cancelled) {
        setIsLoading(false);
        return;
      }
      setUserId(user.id);

      // Load profile for hasChild condition
      const { data: profileData } = await supabase
        .from("user_profiles")
        .select("improvement_focus")
        .eq("user_id", user.id)
        .maybeSingle();

      if (!cancelled && profileData) {
        const focus = profileData.improvement_focus || "";
        setProfile({
          hasChild:
            focus.toLowerCase().includes("baby") ||
            focus.toLowerCase().includes("child") ||
            focus.toLowerCase().includes("beb") ||
            focus.toLowerCase().includes("filho"),
        });
      }

      // Load module preferences
      const { data: prefs } = await (supabase as any)
        .from("user_module_preferences")
        .select("module_id, is_visible, is_favorite, position")
        .eq("user_id", user.id);

      if (!cancelled && prefs) {
        setPreferences(prefs);
      }

      if (!cancelled) setIsLoading(false);
    };

    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // ── Resolve visibility ─────────────────────────────────────────────────

  const isModuleVisible = useCallback(
    (mod: ModuleDefinition): boolean => {
      // Hidden modules never show in UI
      if (mod.category === "hidden") return false;
      return true;
    },
    [],
  );

  // ── Resolve modules with i18n and preference data ──────────────────────

  const resolveModule = useCallback(
    (mod: ModuleDefinition): ResolvedModule => {
      const pref = preferences.find((p) => p.module_id === mod.id);
      return {
        ...mod,
        name: t(`module.${mod.i18nKey}.name`),
        sub: t(`module.${mod.i18nKey}.sub`),
        isFavorite: pref?.is_favorite ?? false,
        position: pref?.position ?? 0,
      };
    },
    [preferences, t],
  );

  // Gate do módulo mywork (allow-list por e-mail, decisão ee013ee6).
  // ⚠️ Aplicado nos TRÊS memos abaixo, não só em isModuleVisible:
  // coreModules/specialModules saem de getModulesByCategory(), que NÃO passa
  // por isModuleVisible — um gate posto só lá não segura o card. Enquanto o
  // e-mail carrega (myWorkLoading), o módulo NÃO aparece — precedente
  // anti-flash do PLANS_V32_LOCK_ENABLED em Modules.tsx (P0 #3): gate defere
  // enquanto carrega.
  const myWorkGate = useCallback(
    (mod: ModuleDefinition): boolean =>
      mod.id !== "mywork" || (myWorkAllowed && !myWorkLoading),
    [myWorkAllowed, myWorkLoading],
  );

  // ── Computed lists ─────────────────────────────────────────────────────

  const visibleModules = useMemo(
    () =>
      MODULE_REGISTRY.filter(isModuleVisible).filter(myWorkGate).map(resolveModule),
    [isModuleVisible, myWorkGate, resolveModule],
  );

  const coreModules = useMemo(
    () =>
      getModulesByCategory("core").filter(myWorkGate).map(resolveModule),
    [myWorkGate, resolveModule],
  );

  const specialModules = useMemo(
    () =>
      getModulesByCategory("special").filter(myWorkGate).map(resolveModule),
    [myWorkGate, resolveModule],
  );

  const favorites = useMemo(
    () =>
      visibleModules
        .filter((m) => m.isFavorite && VALID_MODULE_IDS.has(m.id))
        .sort((a, b) => a.position - b.position),
    [visibleModules],
  );

  // ── Toggle favorite ────────────────────────────────────────────────────

  const toggleFavorite = useCallback(
    async (moduleId: string) => {
      if (!userId) return;

      const existing = preferences.find((p) => p.module_id === moduleId);
      const newIsFavorite = existing ? !existing.is_favorite : true;
      const newPosition = newIsFavorite
        ? Math.max(0, ...preferences.map((p) => p.position)) + 1
        : 0;

      // Optimistic update
      const prevPreferences = [...preferences];
      setPreferences((prev) => {
        const without = prev.filter((p) => p.module_id !== moduleId);
        return [
          ...without,
          {
            module_id: moduleId,
            is_visible: existing?.is_visible ?? true,
            is_favorite: newIsFavorite,
            position: newPosition,
          },
        ];
      });

      // Persist to Supabase
      const { error: err } = await (supabase as any)
        .from("user_module_preferences")
        .upsert(
          {
            user_id: userId,
            module_id: moduleId,
            is_visible: existing?.is_visible ?? true,
            is_favorite: newIsFavorite,
            position: newPosition,
            updated_at: new Date().toISOString(),
          },
          { onConflict: "user_id,module_id" },
        );

      if (err) {
        // Rollback on error
        setPreferences(prevPreferences);
      }
    },
    [userId, preferences],
  );

  return {
    visibleModules,
    coreModules,
    specialModules,
    favorites,
    toggleFavorite,
    isLoading,
  };
}
